import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { motion } from 'framer-motion';

const FooterSection = () => {
  const currentYear = new Date()?.getFullYear();

  const footerLinks = {
    particulieren: {
      title: "Voor Particulieren",
      links: [
        { label: "Persoonlijk advies", path: "/quote-request-form" },
        { label: "Verduurzamingsoplossingen", path: "/services" },
        { label: "Kennisbank", path: "/knowledge" },
        { label: "Subsidies & regelingen", path: "/knowledge/subsidies" },
        { label: "Veelgestelde vragen", path: "/faq" },
        { label: "Klantervaringen", path: "/reviews" }
      ]
    },
    aanbieders: {
      title: "Voor Aanbieders",
      links: [
        { label: "Word aanbieder", path: "/provider-registration" },
        { label: "Aanbieder login", path: "/provider-login" },
        { label: "Werkwijze platform", path: "/how-it-works-providers" },
        { label: "Tarieven & pakketten", path: "/pricing" },
        { label: "Aanbieder support", path: "/provider-support" }
      ]
    },
    services: {
      title: "Diensten",
      links: [
        { label: "Isolatie", path: "/services/insulation" },
        { label: "Warmtepompen", path: "/services/heatpump" },
        { label: "Zonnepanelen", path: "/services/solar" },
        { label: "Thuisbatterijen", path: "/services/battery" },
        { label: "GRP Thuisbatterijen", path: "/grp-thuisbatterijen" },
        { label: "Ventilatie", path: "/services/ventilation" },
        { label: "HR++ / Triple glas", path: "/services/glazing" }
      ]
    },
    company: {
      title: "Over Ons",
      links: [
        { label: "Over ons", path: "/over-ons" },
        { label: "Onze missie", path: "/onze-missie" },
        { label: "Onafhankelijkheid", path: "/onafhankelijkheid" },
        { label: "Werkwijze", path: "/werkwijze" },
        { label: "Contact", path: "/contact" },
        { label: "Carrière", path: "/carriere" },
        { label: "Pers", path: "/pers" }
      ]
    }
  };

  const socialLinks = [
    { name: "Facebook", icon: "Facebook", url: "https://facebook.com/verduurzaamwijzer" },
    { name: "Twitter", icon: "Twitter", url: "https://twitter.com/verduurzaamwijzer" },
    { name: "LinkedIn", icon: "Linkedin", url: "https://linkedin.com/company/verduurzaamwijzer" },
    { name: "Instagram", icon: "Instagram", url: "https://instagram.com/verduurzaamwijzer" },
    { name: "YouTube", icon: "Youtube", url: "https://youtube.com/verduurzaamwijzer" }
  ];

  const certifications = [
    { name: "RVO Erkend", logo: "https://images.pexels.com/photos/159888/pexels-photo-159888.jpeg?auto=compress&cs=tinysrgb&w=80&h=40&dpr=1" },
    { name: "ISSO Gecertificeerd", logo: "https://images.pexels.com/photos/159888/pexels-photo-159888.jpeg?auto=compress&cs=tinysrgb&w=80&h=40&dpr=1" },
    { name: "KvK Geregistreerd", logo: "https://images.pexels.com/photos/159888/pexels-photo-159888.jpeg?auto=compress&cs=tinysrgb&w=80&h=40&dpr=1" },
    { name: "GDPR Compliant", logo: "https://images.pexels.com/photos/159888/pexels-photo-159888.jpeg?auto=compress&cs=tinysrgb&w=80&h=40&dpr=1" }
  ];

  const contactInfo = {
    address: "Oude Middenweg 17\n2491 AC Den Haag\nNederland",
    phone: "06 4765 0537",
    email: "contact@verduurzamewijzer.nu",
    emailAdvies: "advies@verduurzamewijzer.nu",
    kvk: "KvK: 12345678",
    btw: "BTW: NL123456789B01"
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <footer className="bg-secondary text-secondary-foreground">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Company Info */}
          <motion.div 
            className="lg:col-span-2 space-y-6"
            variants={itemVariants}
          >
            {/* Logo */}
            <a href="/homepage" className="flex items-center space-x-2">
              <motion.div 
                className="w-8 h-8 bg-primary rounded-md flex items-center justify-center"
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.5 }}
              >
                <Icon name="Leaf" size={20} color="white" />
              </motion.div>
              <span className="font-heading font-semibold text-xl text-secondary-foreground">
                Verduurzaamwijzer
              </span>
            </a>

            {/* Description */}
            <p className="text-sm font-body text-secondary-foreground/80 leading-relaxed">
              Nederland's grootste platform voor duurzame woningoplossingen. 
              Wij verbinden huiseigenaren met gecertificeerde providers voor 
              zonnepanelen, warmtepompen, isolatie en meer.
            </p>

            {/* Trust Statement */}
            <div className="bg-secondary-foreground/10 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="ShieldCheck" size={16} className="text-success mt-0.5 flex-shrink-0" />
                <p className="text-xs font-body text-secondary-foreground/90 leading-relaxed">
                  <strong>Onafhankelijk & transparant:</strong> Wij ontvangen geen commissies voor advies. 
                  Onze missie is u helpen de beste keuze te maken voor uw woning.
                </p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <Icon name="MapPin" size={16} className="text-secondary-foreground/60 mt-0.5 flex-shrink-0" />
                <div className="text-sm font-body text-secondary-foreground/80 whitespace-pre-line">
                  {contactInfo?.address}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Phone" size={16} className="text-secondary-foreground/60" />
                <a 
                  href={`tel:${contactInfo?.phone}`}
                  className="text-sm font-body text-secondary-foreground/80 hover:text-secondary-foreground transition-colors"
                >
                  {contactInfo?.phone}
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Mail" size={16} className="text-secondary-foreground/60" />
                <a 
                  href={`mailto:${contactInfo?.email}`}
                  className="text-sm font-body text-secondary-foreground/80 hover:text-secondary-foreground transition-colors"
                >
                  {contactInfo?.email}
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Mail" size={16} className="text-secondary-foreground/60" />
                <a 
                  href={`mailto:${contactInfo?.emailAdvies}`}
                  className="text-sm font-body text-secondary-foreground/80 hover:text-secondary-foreground transition-colors"
                >
                  {contactInfo?.emailAdvies}
                </a>
              </div>
            </div>

            {/* Business Registration */}
            <div className="space-y-1 text-xs font-caption text-secondary-foreground/60">
              <div>{contactInfo?.kvk}</div>
              <div>{contactInfo?.btw}</div>
            </div>
          </motion.div>

          {/* Footer Links */}
          {Object.entries(footerLinks)?.map(([key, section], sectionIndex) => (
            <motion.div 
              key={key} 
              className="space-y-4"
              variants={itemVariants}
            >
              <h3 className="text-sm font-heading font-semibold text-secondary-foreground">
                {section?.title}
              </h3>
              <ul className="space-y-2">
                {section?.links?.map((link, index) => (
                  <motion.li 
                    key={index}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <a
                      href={link?.path}
                      className="text-sm font-body text-secondary-foreground/80 hover:text-secondary-foreground transition-colors"
                    >
                      {link?.label}
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Certifications */}
        <motion.div 
          className="mt-12 pt-8 border-t border-secondary-foreground/20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
            <div>
              <h4 className="text-sm font-heading font-medium text-secondary-foreground mb-3">
                Erkende Certificeringen
              </h4>
              <div className="flex flex-wrap items-center gap-4">
                {certifications?.map((cert, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Image
                      src={cert?.logo}
                      alt={cert?.name}
                      className="w-8 h-4 object-contain opacity-80"
                    />
                    <span className="text-xs font-caption text-secondary-foreground/60">
                      {cert?.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="text-sm font-heading font-medium text-secondary-foreground mb-3">
                Volg Ons
              </h4>
              <div className="flex space-x-3">
                {socialLinks?.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-secondary-foreground/10 rounded-full flex items-center justify-center hover:bg-secondary-foreground/20 transition-colors"
                    aria-label={social?.name}
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Icon name={social?.icon} size={16} className="text-secondary-foreground/80" />
                  </motion.a>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      {/* Made Possible By Section */}
      <div className="border-t border-secondary-foreground/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-3"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center space-x-2 text-sm font-body text-secondary-foreground/70">
              <Icon name="Heart" size={16} className="text-primary" />
              <span>Dit platform is mede mogelijk gemaakt door</span>
            </div>
            <motion.a
              href="https://www.verbeterjehuis.nu"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 text-primary rounded-lg font-semibold text-sm hover:bg-primary/20 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>VerbeterJeHuis.nu</span>
              <Icon name="ExternalLink" size={14} />
            </motion.a>
          </motion.div>
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t border-secondary-foreground/20 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <motion.div 
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs font-caption text-secondary-foreground/60">
              © {currentYear} Verduurzaamwijzer. Alle rechten voorbehouden.
            </div>
            <div className="flex items-center space-x-4 text-xs font-caption text-secondary-foreground/60">
              <a 
                href="/privacy" 
                className="hover:text-secondary-foreground/80 transition-colors"
              >
                Privacy
              </a>
              <span>•</span>
              <a 
                href="/voorwaarden" 
                className="hover:text-secondary-foreground/80 transition-colors"
              >
                Voorwaarden
              </a>
              <span>•</span>
              <a 
                href="/transparantie" 
                className="hover:text-secondary-foreground/80 transition-colors"
              >
                Transparantie
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;